import React, { ReactElement, useRef, useState, useEffect, MutableRefObject } from 'react';
import { type SxProps, type Theme } from '@mui/material';
import { Tooltip } from '@/components/Tooltip';

type EllipsisTooltipProps = {
  title?: string;
  children: ReactElement;
  tooltipSx?: SxProps<Theme>;
};

export const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({ title, children, tooltipSx }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const target = (wrapper.firstElementChild as HTMLElement | null) ?? wrapper;

    const checkTruncation = () => {
      if (!target) return;
      const hasOverflow = target.scrollWidth > target.clientWidth;
      setIsTruncated(hasOverflow);
    };

    checkTruncation();

    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [title]);

  const childWithRef = React.cloneElement(children, {
    ref: (node: HTMLDivElement | null) => {
      wrapperRef.current = node;

      const originalRef = (children as any).ref;
      if (typeof originalRef === 'function') {
        originalRef(node);
      } else if (originalRef && typeof originalRef === 'object') {
        (originalRef as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
  });

  if (!title) {
    return childWithRef;
  }

  return (
    <Tooltip
      title={isTruncated ? title : undefined}
      placement="top"
      slotProps={tooltipSx ? { tooltip: { sx: tooltipSx } } : undefined}
    >
      {childWithRef}
    </Tooltip>
  );
};
